
import { useState } from "react";
import { 
  ShoppingCart, 
  X, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowRight
} from "lucide-react";
import { useCartStore } from "@/store/cart";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger,
  SheetFooter,
  SheetClose
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";

export function CartDrawer() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const { 
    items, 
    removeItem, 
    updateQuantity, 
    getSubtotal, 
    getTotal, 
    getItemCount,
    selectedShipping
  } = useCartStore();

  const handleCheckout = () => {
    setOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" className="relative" size="icon">
          <ShoppingCart className="h-5 w-5" />
          {getItemCount() > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
              {getItemCount()}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Seu Carrinho
            {getItemCount() > 0 && (
              <Badge variant="outline">{getItemCount()} {getItemCount() === 1 ? 'item' : 'itens'}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>
        
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <div className="rounded-full bg-muted p-6">
              <ShoppingCart className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium">Seu carrinho está vazio</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Adicione produtos para começar sua compra
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto py-4">
            {items.map((item) => (
              <div key={item.id} className="flex gap-4 py-4">
                <div className="h-24 w-24 rounded-md overflow-hidden">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title} 
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h4 className="font-medium">{item.title}</h4>
                    <div className="flex items-baseline mt-1">
                      {item.discountedPrice ? (
                        <>
                          <span className="font-medium">R$ {item.discountedPrice.toFixed(2)}</span>
                          <span className="ml-2 text-xs text-muted-foreground line-through">
                            R$ {item.price.toFixed(2)}
                          </span>
                        </>
                      ) : (
                        <span className="font-medium">R$ {item.price.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border rounded-md">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-none"
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-none"
                        onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {items.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2 py-4">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>R$ {getSubtotal().toFixed(2)}</span>
              </div>
              {selectedShipping && (
                <div className="flex justify-between">
                  <span>Frete ({selectedShipping.name})</span>
                  <span>R$ {selectedShipping.price.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium text-lg">
                <span>Total</span>
                <span>R$ {getTotal().toFixed(2)}</span>
              </div>
            </div>
            <SheetFooter className="sm:justify-center mt-auto">
              <Button 
                onClick={handleCheckout} 
                className="w-full"
              >
                Finalizar Compra <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
